import {IssueViewer} from '@github-ui/issue-viewer/IssueViewer'
import {ISSUE_VIEWER_DEFAULT_CONFIG, type IssueViewerPreloadedQueries} from '@github-ui/issue-viewer/OptionConfig'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {noop} from '@github-ui/noop'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {RelayCachePreheatContextProvider} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import type {SubIssueSidePanelItem} from '@github-ui/sub-issues/sub-issue-types'
import {useHovercardClickIntercept} from '@github-ui/use-link-interception/use-hovercard-click-intercept'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {ScreenFullIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {Suspense, useCallback, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {EMPTY_VIEW} from '../../constants/view-constants'
import {useAppNavigate} from '../../hooks/use-app-navigate'
import {useRouteInfo} from '../../hooks/use-route-info'
import type {AppPayload} from '../../types/app-payload'
import {isUrlInRepoIssuesContext, timelineEventBaseUrl} from '../../utils/urls'
import type {IssueDetailCurrentViewFragment$key} from './__generated__/IssueDetailCurrentViewFragment.graphql'
import styles from './IssueDetail.module.css'
import {IssueSidePanel} from './IssueSidePanel'

type IssueDetailProps = {
  fetchedView: IssueDetailCurrentViewFragment$key
  preloadedQueries: IssueViewerPreloadedQueries
}

export const IssueDetail = ({fetchedView, preloadedQueries}: IssueDetailProps) => {
  const {itemIdentifier, viewId, sidePanelItemIdentifier, setSidePanelItemIdentifier, sidePanelItemURL} = useRouteInfo()
  const {onIssueHrefLinkClick, navigateToRoot} = useAppNavigate()
  const {query: viewQuery} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueDetailCurrentViewFragment on Shortcutable {
        name
        query
      }
    `,
    fetchedView, // check for null above
  )

  const appPayload = useAppPayload<AppPayload>()
  const showRepositoryPill = !appPayload.scoped_repository

  const navigateBack = useCallback(() => {
    navigateToRoot(viewId ?? EMPTY_VIEW.id, viewQuery)
  }, [viewId, viewQuery, navigateToRoot])
  const {navigateToUrl} = useAppNavigate()

  const onSubIssueClick = useCallback(
    (subIssueItem: SubIssueSidePanelItem) => {
      const {owner, repo, number} = subIssueItem
      setSidePanelItemIdentifier({owner, repo, number, type: 'Issue'})
    },
    [setSidePanelItemIdentifier],
  )
  const onCloseSidePanel = useCallback(() => {
    setSidePanelItemIdentifier(null)
  }, [setSidePanelItemIdentifier])

  const onIssueNavigate = useCallback(
    (issueIdentifier: {owner: string; repo: string; number: number}) => {
      setSidePanelItemIdentifier({...issueIdentifier, type: 'Issue'})
    },
    [setSidePanelItemIdentifier],
  )

  const [_, setDeletedRecord] = useSessionStorage<string>(
    `${appPayload.scoped_repository?.owner}-${appPayload.scoped_repository?.name}-deletedRecordId`,
    '',
  )

  const onIssueDelete = useCallback(
    (issueId: string) => {
      setDeletedRecord(issueId)
    },
    [setDeletedRecord],
  )

  const onParentIssueActivate = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent, parentIdentifier: ItemIdentifier): boolean => {
      if (!itemIdentifier) return false

      // Only close the side panel if the parent is the same as the current issue
      if (
        parentIdentifier.owner !== itemIdentifier.owner ||
        parentIdentifier.repo !== itemIdentifier.repo ||
        parentIdentifier.number !== itemIdentifier.number
      ) {
        return false
      }

      e.preventDefault()
      onCloseSidePanel()
      return true
    },
    [itemIdentifier, onCloseSidePanel],
  )

  useHovercardClickIntercept((targetUrl, event) => {
    // Soft navigation works only for issues in the same repo
    if (isUrlInRepoIssuesContext(targetUrl, itemIdentifier?.owner || '', itemIdentifier?.repo || '')) {
      return
    }

    const link = (event.target as HTMLElement).closest('a')
    if (!link) return

    navigateToUrl(link)
  })

  const tabbedTimelineConfig = useMemo(() => {
    const tabbedTimeline = Boolean(
      appPayload.conversation_events?.length || appPayload.activity_events?.length || appPayload.tabbed_timeline,
    )

    return {tabbedTimeline} as const
  }, [appPayload.conversation_events, appPayload.activity_events, appPayload.tabbed_timeline])

  const scopedRepo = appPayload.scoped_repository

  const primaryOptionConfig = useMemo(
    () => ({
      showRepositoryPill,
      showIssueCreateButton: true,
      onIssueUpdate: noop,
      onLinkClick: onIssueHrefLinkClick,
      onRemovedFromProject: noop,
      navigateBack,
      onIssueDelete,
      navigate: navigateToUrl,
      preloadedQueries,
      timelineEventBaseUrl: timelineEventBaseUrl(scopedRepo?.name, scopedRepo?.owner),
      scrollToTopOnClick: true,
      onSubIssueClick,
      onIssueNavigate,
      useViewportQueries: true, // This is a full page view, so we want to use viewport queries
      /*
       * Toggling between false and undefined allows us to distinguish if the side panel is open or not
       * from the full page view without adding an additional prop
       */
      insideSidePanel: sidePanelItemIdentifier ? false : undefined,
      ...tabbedTimelineConfig,
    }),
    [
      showRepositoryPill,
      onIssueHrefLinkClick,
      navigateBack,
      onIssueDelete,
      navigateToUrl,
      preloadedQueries,
      onSubIssueClick,
      onIssueNavigate,
      scopedRepo?.name,
      scopedRepo?.owner,
      sidePanelItemIdentifier,
      tabbedTimelineConfig,
    ],
  )

  return (
    <>
      <div className={clsx('primary-viewer', styles.IssueDetailContainer)} data-testid={TEST_IDS.issueViewerContainer}>
        <Suspense fallback={<span>Loading Issue...</span>}>
          {itemIdentifier && (
            <RelayCachePreheatContextProvider>
              <IssueViewer itemIdentifier={itemIdentifier} optionConfig={primaryOptionConfig} />
            </RelayCachePreheatContextProvider>
          )}
        </Suspense>
      </div>
      {sidePanelItemIdentifier && (
        <IssueSidePanel onClose={onCloseSidePanel}>
          <IssueViewer
            itemIdentifier={sidePanelItemIdentifier}
            optionConfig={Object.assign({}, ISSUE_VIEWER_DEFAULT_CONFIG, {
              shouldSkipSetDocumentTitle: true,
              onClose: onCloseSidePanel,
              insideSidePanel: true,
              onIssueUpdate: noop,
              onRemovedFromProject: noop,
              onSubIssueClick,
              onIssueNavigate,
              onParentIssueActivate,
              onIssueDelete,
              navigateBack: onCloseSidePanel,
              showRepositoryPill:
                sidePanelItemIdentifier.owner !== itemIdentifier?.owner ||
                sidePanelItemIdentifier.repo !== itemIdentifier?.repo,
              ...tabbedTimelineConfig,
              additionalHeaderActions: (
                <IconButton
                  as="a"
                  role="link"
                  variant="invisible"
                  icon={ScreenFullIcon}
                  aria-label={LABELS.sidePanelTooltip}
                  href={sidePanelItemURL}
                />
              ),
            })}
          />
        </IssueSidePanel>
      )}
    </>
  )
}
